/**
 * useModalLayer Hook
 * 
 * Provides easy integration with the Modal Layer Manager
 * Handles registration, cleanup, and state management
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { modalLayerManager, MODAL_LAYERS } from '../components/modals/core/ModalLayerManager';

export const useModalLayer = (modalType, isOpen, options = {}) => {
  const modalIdRef = useRef(`${modalType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [isMinimized, setIsMinimized] = useState(false);
  const [shouldClose, setShouldClose] = useState(false);
  
  const {
    layer = MODAL_LAYERS[modalType.toUpperCase()] || MODAL_LAYERS.RESULTS,
    onCloseRequest = null,
    disabled = false, // New option to disable modal layer management
    ...otherOptions
  } = options;

  // Store callbacks and options in refs to avoid re-registering on every render
  const onCloseRequestRef = useRef(onCloseRequest);
  const otherOptionsRef = useRef(otherOptions);
  onCloseRequestRef.current = onCloseRequest;
  otherOptionsRef.current = otherOptions;
  
  // Register/unregister modal with the manager (unless disabled)
  // Only re-register when isOpen, disabled, modalType, or layer changes
  useEffect(() => {
    if (isOpen && !disabled) {
      const modalId = modalIdRef.current; // Copy ref value to avoid stale closure
      
      modalLayerManager.registerModal(
        modalId,
        modalType,
        layer,
        otherOptionsRef.current
      );

      // Listen for close requests from the manager
      const unsubscribe = modalLayerManager.subscribe((event) => {
        if (event.modalId === modalId && event.action === 'CLOSE_REQUEST') {
          console.log(`🚪 Close requested for ${modalType} modal:`, event.reason);
          setShouldClose(true);
          if (onCloseRequestRef.current) {
            onCloseRequestRef.current(event.reason);
          }
        }
      });

      return () => {
        unsubscribe();
        modalLayerManager.unregisterModal(modalId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, disabled, modalType, layer]); // onCloseRequest and otherOptions intentionally excluded - using refs instead

  // Update modal state when minimize status changes (unless disabled)
  useEffect(() => {
    if (isOpen && !disabled) {
      modalLayerManager.updateModal(modalIdRef.current, { isMinimized });
    }
  }, [isOpen, disabled, isMinimized]);

  // Handle minimize/restore
  const minimize = useCallback(() => {
    console.log(`📉 Minimizing ${modalType} modal`);
    setIsMinimized(true);
  }, [modalType]);

  const restore = useCallback(() => {
    console.log(`📈 Restoring ${modalType} modal`);
    setIsMinimized(false);
  }, [modalType]);

  // Get current z-index for this modal (null when disabled)
  const zIndex = disabled ? null : modalLayerManager.getZIndexForLayer(layer);

  // Reset close request when modal reopens
  useEffect(() => {
    if (isOpen) {
      setShouldClose(false);
    }
  }, [isOpen]);

  return {
    modalId: modalIdRef.current,
    isMinimized,
    shouldClose,
    zIndex,
    minimize,
    restore,
    // Modal layer management methods (no-ops when disabled) - only include updateModal as it's used
    updateModal: disabled ? () => {} : (id, updates) => modalLayerManager.updateModal(id, updates),
    // Helper methods
    isBodyLocked: modalLayerManager.bodyLocked,
    getManagerState: () => modalLayerManager.getState()
  };
};
